/**
 * ProsConsSiteColumn — single column in the workspace, dedicated to one
 * site. Top half is the Pros panel, bottom half is the Cons panel.
 *
 * Sort:
 *   - pros desc by upvotes (ties → most-recent first)
 *   - cons desc by impactLevel (HIGH > MEDIUM > LOW > unset)
 *
 * Linear: MYD-223 (H1).
 */

import { useMemo } from 'react'
import { Card } from 'components/ui/card'
import { Button } from 'components/ui/button'
import { Plus, CheckCircle2, XCircle } from 'lucide-react'
import { cn } from 'lib/utils'
import ProConItem from './ProConItem'

const IMPACT_RANK: Record<string, number> = { HIGH: 3, MEDIUM: 2, LOW: 1 }

interface ProsConsSiteColumnProps {
	site: { _id: string; name: string }
	items: any[]
	loading?: boolean
	onAdd: (siteId: string, type: 'PRO' | 'CON') => void
	onEdit: (item: any) => void
	onDelete: (siteId: string, itemId: string) => void
	onVote: (siteId: string, itemId: string) => void
}

const ProsConsSiteColumn = ({
	site,
	items,
	loading,
	onAdd,
	onEdit,
	onDelete,
	onVote,
}: ProsConsSiteColumnProps) => {
	const pros = useMemo(
		() =>
			items
				.filter((i) => i.type === 'PRO')
				.slice()
				.sort((a, b) => (b.votes ?? 0) - (a.votes ?? 0)),
		[items],
	)
	const cons = useMemo(
		() =>
			items
				.filter((i) => i.type === 'CON')
				.slice()
				.sort(
					(a, b) =>
						(IMPACT_RANK[b.impactLevel] ?? 0) - (IMPACT_RANK[a.impactLevel] ?? 0),
				),
		[items],
	)

	const Panel = ({
		title,
		list,
		type,
		Icon,
		accentClass,
		emptyText,
	}: {
		title: string
		list: any[]
		type: 'PRO' | 'CON'
		Icon: typeof CheckCircle2
		accentClass: string
		emptyText: string
	}) => (
		<div className="flex flex-col flex-1 min-h-0">
			<div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
				<h5
					className={cn(
						'text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5',
						accentClass,
					)}
				>
					<Icon className="w-3.5 h-3.5" />
					{title} ({list.length})
				</h5>
				<Button
					variant="primary"
					onClick={() => onAdd(site._id, type)}
					className="gap-1 h-7 px-2 text-xs"
				>
					<Plus className="w-3 h-3" />
					Add
				</Button>
			</div>
			<div className="flex-1 overflow-y-auto px-2 py-2 space-y-1.5 bg-gray-50/40">
				{list.length === 0 ? (
					<div className="text-center py-4 text-xs text-gray-400">{emptyText}</div>
				) : (
					list.map((item) => (
						<ProConItem
							key={item._id}
							item={item}
							onVote={(id) => onVote(site._id, id)}
							onEdit={onEdit}
							onDelete={(id) => onDelete(site._id, id)}
						/>
					))
				)}
			</div>
		</div>
	)

	return (
		<Card className="flex flex-col w-[320px] shrink-0 lg:w-auto lg:shrink lg:flex-1 min-h-[480px]">
			<div className="px-3 py-2.5 border-b border-gray-200 bg-white sticky top-0 z-10 rounded-t-lg">
				<h4 className="text-sm font-semibold text-gray-900 truncate" title={site.name}>
					{site.name}
				</h4>
				{loading && <p className="text-[10px] text-gray-400 mt-0.5">Loading…</p>}
			</div>
			<Panel
				title="Pros"
				list={pros}
				type="PRO"
				Icon={CheckCircle2}
				accentClass="text-green-700"
				emptyText="No pros yet."
			/>
			<Panel
				title="Cons"
				list={cons}
				type="CON"
				Icon={XCircle}
				accentClass="text-red-700"
				emptyText="No cons yet."
			/>
		</Card>
	)
}

export default ProsConsSiteColumn
